import { Link } from "react-router-dom";
import {
  ArrowRight, CalendarCheck, LayoutDashboard, ListTree, ClipboardList, GitBranch, Wallet,
  Radar, FileDown, CloudOff, ScanText, Package, Users,
} from "lucide-react";
import { Logo, TAGLINE } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import TrustBar from "@/components/TrustBar";
import PublicHeader from "@/components/PublicHeader";
import { useMeta } from "@/lib/useMeta";
import { INDUSTRY_LIST } from "@/pages/industry/industryData";

const MODULES = [
  {
    id: "command-center",
    icon: LayoutDashboard,
    kicker: "Command Center",
    title: "One screen your client review can run on",
    body:
      "Physical progress, planned versus actual S-curve, CPI and SPI, cash-out curve and the day's manpower — all computed from validated field data, never from a percentage someone typed in. Open it on Monday morning and the meeting runs itself.",
    points: ["Live S-curve with baseline and forecast", "CPI / SPI on physical progress", "Red work fronts surfaced first"],
    shot: "command-center.png",
  },
  {
    id: "dpr",
    icon: ClipboardList,
    kicker: "Daily Progress Report",
    title: "The field log that drives every other number",
    body:
      "The site engineer records quantity executed, shift, skilled/semi/unskilled split, planned manpower, idle hours and weather. The moment it is validated, balance quantities, run-rates, the S-curve, earned value and the forecast completion date all recompute. Nobody reconciles anything by hand.",
    points: ["Quantity, manpower and idle-hour capture", "Validation workflow before figures count", "Works with no network signal and syncs later"],
    shot: "dpr.png",
  },
  {
    id: "wbs",
    icon: ListTree,
    kicker: "WBS & Quantities",
    title: "Scope in the units your contract actually uses",
    body:
      "A three-level work breakdown with real quantities, units and rates — Cu.M of concrete, MT of structural steel, Dia-Inch of piping, Nos of plates. Balance quantity, executed value and progress are derived, not maintained.",
    points: ["L1 / L2 / L3 hierarchy with codes", "Discipline-wise rollups", "Balance quantity computed automatically"],
    shot: "wbs.png",
  },
  {
    id: "critical-path",
    icon: GitBranch,
    kicker: "Critical Path Engine",
    title: "Float you can defend in a claim",
    body:
      "A full forward and backward pass gives early and late dates, total and free float, and marks the activities driving your completion date. The network diagram shows the logic; the schedule table gives the numbers an extension-of-time notice needs.",
    points: ["ES / EF / LS / LF and float per activity", "Interactive activity network diagram", "Critical chain highlighted end to end"],
    shot: "critical-path.png",
  },
  {
    id: "costing",
    icon: Wallet,
    kicker: "Costing & EVM",
    title: "Earned value tied to physical progress",
    body:
      "BAC, PV, EV, AC, cost and schedule variance, CPI, SPI, EAC and VAC — plus the two leaks most contractors never price: idle-time cost and hindrance exposure. You see an overrun forming while there is still time to act on it.",
    points: ["Full EVM set on real executed quantity", "Cost composition with overhead loading", "Idle-time and hindrance cost leakage"],
    shot: "costing.png",
  },
  {
    id: "risk-radar",
    icon: Radar,
    kicker: "Risk Radar (AI)",
    title: "The delay, called weeks before it lands",
    body:
      "Every front's trailing 21-day run-rate is compared with what the remaining scope demands. Fronts are banded red, amber or green, each with a forecast finish and days-late figure, and the AI writes the management brief: verdict, causes and specific recovery actions.",
    points: ["Forecast completion and projected overrun", "Value at risk per work front", "AI verdict with recovery actions"],
    shot: "risk-radar.png",
    paid: true,
  },
  {
    id: "planning",
    icon: Users,
    kicker: "Tiered Planning",
    title: "Yearly intent, broken down to this week's target",
    body:
      "L1 annual targets cascade into monthly and weekly commitments per work front, and achievement is measured against the DPR — so a slipping week is visible on the Friday, not at the month-end review.",
    points: ["L1 / L2 / L3 target cascade", "Weekly achievement versus commitment", "Resource and manpower planning"],
    shot: "planning.png",
  },
  {
    id: "export-center",
    icon: FileDown,
    kicker: "Export Center",
    title: "The reporting pack, in one click",
    body:
      "A seven-sheet Excel data pack, a board-ready PowerPoint deck, A4 and A3 print PDFs, and a live Power BI feed — generated from the same validated data, so no two reports ever disagree.",
    points: ["Excel, PPTX and print-ready PDF", "Power BI / OData live feed", "Same numbers everywhere"],
    shot: "export-center.png",
  },
];

const EXTRAS = [
  [CloudOff, "Works with no signal", "Site engineers log DPRs in zero-connectivity zones; entries sit on the device and sync themselves the moment a bar of signal returns."],
  [ScanText, "AI BOQ Parser", "Drop in an Excel, CSV or PDF BOQ. Two engines extract it, units are normalised, confidence is scored per row — you review and confirm, and it becomes a live WBS."],
  [Package, "Store & procurement", "Material inward, issue, consumption against activity and reorder levels, so a stock-out shows up as a schedule risk instead of a surprise."],
  [Users, "Employee KPIs", "Output per engineer per work front, so performance conversations are based on executed quantity rather than impressions."],
];

const Shot = ({ file, alt }) => (
  <div className="border border-border overflow-hidden bg-card shadow-2xl shadow-black/20">
    <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border bg-muted/50">
      <span className="w-2 h-2 rounded-full bg-red-500/70" />
      <span className="w-2 h-2 rounded-full bg-amber-500/70" />
      <span className="w-2 h-2 rounded-full bg-emerald-500/70" />
      <span className="num text-[10px] text-muted-foreground ml-2">plansarthi.com</span>
    </div>
    <img src={`/shots/${file}`} alt={alt} loading="lazy" className="w-full block" data-testid={`shot-${file.replace(".png", "")}`} />
  </div>
);

export default function Features() {
  useMeta({
    title: "Features — EPC Project Management Software India | Plan-Sarthi",
    description:
      "See Plan-Sarthi's modules on real screens: Command Center, DPR, WBS, critical path, EVM costing, Risk Radar, AI BOQ parser and one-click reporting for Indian EPC projects.",
    path: "/features",
  });
  return (
    <div className="min-h-screen flex flex-col" data-testid="features-page">
      <PublicHeader />
      <header className="h-14 border-b border-border flex items-center gap-3 px-4 sm:px-6 sticky top-14 md:top-16 z-20 bg-background/85 backdrop-blur-md">
        <span className="text-[12px] uppercase tracking-[0.16em] text-muted-foreground">Platform tour</span>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/demo"><Button size="sm" variant="outline" data-testid="features-demo-btn"><CalendarCheck className="w-3.5 h-3.5 mr-1.5" /> Book a demo</Button></Link>
          <Link to="/signup"><Button size="sm" data-testid="features-signup-btn">Start free trial</Button></Link>
        </div>
      </header>

      <main className="flex-1">
        <section className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-14 pb-10">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#F97316]" data-testid="features-kicker">The platform, screen by screen</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.08] mt-4 max-w-[860px]">
            Your site already produces the data.<br />
            <span className="text-[#F97316]">Plan-Sarthi turns it into control.</span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-5 max-w-[720px] leading-relaxed">
            These are real screens from a live nuclear containment package — not mockups. One validated Daily Progress
            Report recomputes the S-curve, the critical path, earned value and the forecast completion date. {TAGLINE}
          </p>
          <div className="flex flex-wrap gap-2.5 mt-7">
            <Link to="/demo"><Button data-testid="features-hero-demo-btn"><CalendarCheck className="w-4 h-4 mr-2" /> Book a live 30-min demo</Button></Link>
            <Link to="/signup"><Button variant="outline" data-testid="features-hero-trial-btn">Start the 14-day trial <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </section>

        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 pb-6 space-y-16 sm:space-y-24">
          {MODULES.map((m, i) => (
            <section
              key={m.id}
              id={m.id}
              className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center scroll-mt-20 ${i % 2 ? "lg:[&>div:first-child]:order-2" : ""}`}
              data-testid={`feature-${m.id}`}
            >
              <div>
                <div className="flex items-center gap-2.5">
                  <m.icon className="w-4 h-4 text-[#F97316]" />
                  <span className="text-[11px] uppercase tracking-[0.16em] text-[#F97316]">{m.kicker}</span>
                  {m.paid ? (
                    <span className="text-[10px] uppercase tracking-wider border border-[#F97316]/50 text-[#F97316] px-1.5 py-0.5">Paid plan</span>
                  ) : null}
                </div>
                <h2 className="text-base md:text-lg font-semibold mt-3 leading-snug">{m.title}</h2>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{m.body}</p>
                <ul className="mt-4 space-y-2">
                  {m.points.map((p) => (
                    <li key={p} className="flex gap-2.5 text-[13px]">
                      <span className="text-[#F97316] shrink-0">—</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Shot file={m.shot} alt={`${m.kicker} screen in Plan-Sarthi`} />
            </section>
          ))}
        </div>

        <section className="max-w-[1200px] mx-auto px-5 sm:px-8 py-16" id="boq-parser">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-wider text-[#F97316]">Built for site reality</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
            {EXTRAS.map(([Icon, title, text]) => (
              <div key={title} className="border border-border bg-card p-4 rise" data-testid={`extra-${title.replace(/\s/g, "-").toLowerCase()}`}>
                <Icon className="w-5 h-5 text-[#F97316]" />
                <h3 className="font-semibold mt-2.5 text-[13.5px]">{title}</h3>
                <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-[1200px] mx-auto px-5 sm:px-8 pb-16" id="predictive-analytics">
          <h2 className="text-base md:text-lg font-semibold uppercase tracking-wider text-[#F97316]">Industries we are built for</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
            {INDUSTRY_LIST.map((ind) => (
              <Link
                key={ind.slug}
                to={`/industry/${ind.slug}`}
                className="flex items-start gap-3 border border-border bg-card p-4 hover:border-[#F97316]/50 transition-colors duration-200"
                data-testid={`features-industry-${ind.slug}`}
              >
                <ind.icon className="w-4 h-4 mt-0.5 shrink-0 text-[#F97316]" />
                <div>
                  <div className="font-medium text-[13.5px]">{ind.name}</div>
                  <div className="text-[11.5px] text-muted-foreground mt-1 line-clamp-2">{ind.tagline}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="max-w-[1200px] mx-auto px-5 sm:px-8 pb-16">
          <div className="border border-[#F97316]/40 bg-[#F97316]/5 p-7 sm:p-9 text-center" data-testid="features-cta">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold">See it on your own project</h2>
            <p className="text-sm text-muted-foreground mt-3 max-w-[620px] mx-auto leading-relaxed">
              Bring a live BOQ or work-front list to a 30-minute call and we will run it through the platform while you
              watch. Or start the 14-day trial — no card, no obligation.
            </p>
            <div className="flex flex-wrap gap-2.5 justify-center mt-6">
              <Link to="/demo"><Button data-testid="features-cta-demo-btn"><CalendarCheck className="w-4 h-4 mr-2" /> Book a live demo</Button></Link>
              <Link to="/contact"><Button variant="outline" data-testid="features-cta-contact-btn">Talk to us</Button></Link>
            </div>
          </div>
        </section>
      </main>

      <TrustBar />
      <Footer />
      <BackToTop />
    </div>
  );
}
